import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import toast from "react-hot-toast";
import { HiOutlineArrowLeft } from "react-icons/hi";
import * as Yup from "yup";
import {
  FloatingInput,
  Loader,
  SearchableDropdown,
} from "../../Components/Common";
import * as routes from "../../routes";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import {
  LEAVE_CATEGORY_OPTIONS,
  LEAVE_CREDIT_TYPES,
  LEAVE_REQUEST_MODE,
  LEAVE_REQUEST_VALIDATION_MESSAGES,
  TOAST_MESSAGES,
} from "./constants";
import {
  createLeaveCreditDebit,
  getLeaveCreditDebitById,
} from "../../Services/LeaveServices";
import { formatMonthYear, toProperCase } from "../../Utils";
import { useESS } from "../ESS/Hooks/useESS";
import DatePicker from "react-multi-date-picker";
import "react-multi-date-picker/styles/layouts/mobile.css";
import DateObject from "react-date-object";

const LeaveCreditDebitActions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getUsers } = useESS();
  const [mode, setMode] = useState(LEAVE_REQUEST_MODE.ADD);
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leaveDates, setLeaveDates] = useState([]);
  const leaveDatesForCalendar = React.useMemo(() => {
    if (!initialValues?.leave_dates) return [];
    return initialValues.leave_dates.map((d) => new DateObject(d));
  }, [initialValues]);

  const isViewMode = mode === LEAVE_REQUEST_MODE.VIEW;
  const filters = location.state?.filters || {
    leave_credit_type: "",
    category: "",
    user: "",
  };

  const fromPage = location.state?.fromPage || routes.LEAVE_CREDIT_DEBIT_LIST;

  useEffect(() => {
    if (location.pathname.includes("view")) setMode(LEAVE_REQUEST_MODE.VIEW);
    else setMode(LEAVE_REQUEST_MODE.ADD);
  }, [location.pathname]);

  useEffect(() => {
    if (mode === LEAVE_REQUEST_MODE.VIEW) {
      const id = location.pathname.split("/").pop();
      setLoading(true);
      getLeaveCreditDebitById(id)
        .then((res) => setInitialValues(res.data))
        .catch(() => toast.error(TOAST_MESSAGES.FAILED_TO_FETCH_DETAILS))
        .finally(() => setLoading(false));
    } else {
      const today = new Date();
      const currentMonth = today.toISOString().slice(0, 7); // "YYYY-MM"

      setInitialValues({
        user: "",
        month: currentMonth,
        category: "",
        leave_credit_type: "",
        leave_credit_count: "",
        leave_dates: [],
      });
    }
  }, [mode, location.pathname]);

  if (loading || !initialValues) {
    return (
      <div className="flex justify-center pt-10">
        <Loader size={4} />
      </div>
    );
  }

  const validationSchema = Yup.object().shape({
    user: Yup.string().required(LEAVE_REQUEST_VALIDATION_MESSAGES.USER),
    month: Yup.string()
      .required(LEAVE_REQUEST_VALIDATION_MESSAGES.MONTH)
      .test(
        "not-past-month",
        "Cannot credit or debit past months",
        function (value) {
          if (!value) return false;

          const selected = new Date(value + "-01");
          const today = new Date();
          const currentMonthStart = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
          );

          return selected >= currentMonthStart;
        },
      ),
    category: Yup.string().required(LEAVE_REQUEST_VALIDATION_MESSAGES.CATEGORY),
    leave_credit_type: Yup.string().required(
      LEAVE_REQUEST_VALIDATION_MESSAGES.LEAVE_CREDIT_TYPE,
    ),
    leave_credit_count: Yup.string().required(
      LEAVE_REQUEST_VALIDATION_MESSAGES.LEAVE_CREDIT_COUNT,
    ),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!leaveDates || leaveDates.length === 0) {
      toast.error(LEAVE_REQUEST_VALIDATION_MESSAGES.LEAVE_DATE_REQUIRED);
      setSubmitting(false);
      return;
    }
    try {
      const [yearStr, monthStr] = values.month.split("-");
      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10); // 1-12

      const start_date = `${yearStr}-${monthStr.padStart(2, "0")}-01`;

      // JS months: 0-indexed, so month = month from split
      const lastDay = new Date(year, month, 0).getDate(); // day 0 → last day of previous month
      const end_date = `${yearStr}-${monthStr.padStart(2, "0")}-${lastDay
        .toString()
        .padStart(2, "0")}`;

      const formData = new FormData();
      formData.append("user", values.user);
      formData.append("start_date", start_date);
      formData.append("end_date", end_date);
      formData.append("category", values.category);
      formData.append("leave_credit_type", values.leave_credit_type);
      formData.append("leave_credit_count", values.leave_credit_count);

      const leaveDatesArray = leaveDates.map((date) =>
        date.format("YYYY-MM-DD"),
      );

      // Append the whole array as a single field
      formData.append("leave_dates", JSON.stringify(leaveDatesArray));

      const response = await createLeaveCreditDebit(formData);

      if (response?.status === 201 || response?.status === 200) {
        toast.success(
          `Leave ${toProperCase(values.leave_credit_type)}ed successfully`,
        );
        navigate(routes.LEAVE_CREDIT_DEBIT_LIST);
      } else {
        const msg =
          extractErrorMessage(response) ||
          `Failed ${toProperCase(values.leave_credit_type)} Leave`;
        toast.error(msg);
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) ||
        `Failed ${toProperCase(values.leave_credit_type)} Leave`;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(fromPage, { state: { filters } });
  };

  if (loading || !initialValues)
    return (
      <div className="flex justify-center pt-10">
        <Loader size={4} />
      </div>
    );

  const commonInput =
    "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-secondary";

  return (
    <div className="w-full max-w-xl mx-auto bg-white p-6 md:p-8 rounded-lg shadow-md mt-8">
      <div className="flex items-center justify-between mb-6">
        <button onClick={handleBack} className="text-primary text-2xl">
          <HiOutlineArrowLeft />
        </button>
        <h2 className="text-xl font-bold text-primary text-center flex-1">
          {isViewMode ? "Leave Details" : "Credit/Debit Leave"}
        </h2>
        <div className="w-6" />
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={isViewMode ? null : validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form className="flex flex-col gap-1">
            {/* Add Mode */}
            {!isViewMode && (
              <>
                <div className="flex justify-between gap-4 w-full">
                  <div className="w-[50%]">
                    <label>
                      User<sub className="text-red-500">{" * "}</sub>
                      <SearchableDropdown
                        fetchFunction={({ search }) =>
                          getUsers({ mode: "all", search })
                        }
                        onSelect={(value) => setFieldValue("user", value.id)}
                        valueKey="id"
                        labelKey="first_name"
                        placeholder={"Select User"}
                        includeAllOption={true}
                        allOptionLabel={"All Users"}
                      />{" "}
                    </label>
                    <ErrorMessage
                      name="user"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                  {/* Category */}
                  <div className="w-[50%]">
                    <label className="text-sm font-semibold text-secondary mb-1 block">
                      {"Category"}
                      <sub className="text-red-500">{" * "}</sub>
                    </label>
                    <Field
                      as="select"
                      name="category"
                      className={commonInput}
                      disabled={
                        isSubmitting || LEAVE_CATEGORY_OPTIONS.length === 0
                      }
                    >
                      <option value="">
                        {LEAVE_CATEGORY_OPTIONS.length === 0
                          ? "No Category available"
                          : "Select Category"}
                      </option>
                      {LEAVE_CATEGORY_OPTIONS.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="category"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                </div>

                <div className="w-full">
                  <label className="text-sm font-semibold text-secondary mb-1 block">
                    Month <sub className="text-red-500">*</sub>
                  </label>
                  <Field
                    type="month"
                    name="month"
                    className={commonInput}
                    min={new Date().toISOString().slice(0, 7)}
                    disabled={isSubmitting}
                  />
                  <ErrorMessage
                    name="month"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                <div className="flex justify-between w-full gap-4">
                  <div className="w-[50%]">
                    <label className="text-sm font-semibold text-secondary mb-1 block">
                      {"Type"}
                      <sub className="text-red-500">{" * "}</sub>
                    </label>
                    <Field
                      as="select"
                      name="leave_credit_type"
                      placeholder="Enter Type..."
                      className={commonInput}
                      disabled={isSubmitting || LEAVE_CREDIT_TYPES.length === 0}
                    >
                      <option value="">
                        {LEAVE_CREDIT_TYPES.length === 0
                          ? "No Type available"
                          : "Select Type"}
                      </option>
                      {LEAVE_CREDIT_TYPES.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="leave_credit_type"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                  <div className="w-[50%]">
                    <label className="text-sm font-semibold text-secondary mb-1 block">
                      {"Count"}
                      <sub className="text-red-500">{" * "}</sub>
                    </label>
                    <Field
                      type="number"
                      name="leave_credit_count"
                      placeholder="Enter count..."
                      className={commonInput}
                      disabled={isSubmitting}
                    />
                    <ErrorMessage
                      name="leave_credit_count"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                </div>
                <div className="w-full">
                  <label className="text-sm font-semibold text-secondary mb-1 block">
                    Leave Dates
                  </label>

                  <DatePicker
                    multiple
                    value={leaveDates}
                    calendarClassName="w-full"
                    containerClassName="w-full"
                    format="DD/MM/YYYY"
                    onChange={(dates) => {
                      setLeaveDates(dates);
                      setFieldValue(
                        "leave_dates",
                        dates?.map((d) => d.format("YYYY-MM-DD")),
                      );
                    }}
                    className={commonInput} // wrapper styles
                    inputClass="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-secondary w-full"
                  />
                </div>
              </>
            )}
            {/* View Mode */}
            {isViewMode && (
              <>
                <div className="flex justify-between gap-4 w-full">
                  <div className="w-[50%]">
                    <FloatingInput
                      label={"User"}
                      name="user"
                      value={toProperCase(initialValues.user_name || "-")}
                      disabled
                    />
                  </div>
                  <div className="w-[50%]">
                    <FloatingInput
                      label={"Category"}
                      name="category"
                      value={toProperCase(
                        initialValues.category + " Leave" || "-",
                      )}
                      disabled
                    />
                  </div>
                </div>

                <div className="w-full flex justify-between gap-4">
                  <div className="w-full">
                    <FloatingInput
                      label={"Month"}
                      name="month"
                      value={formatMonthYear(initialValues.start_date)}
                      disabled
                    />
                  </div>
                </div>

                <div className="w-full flex justify-between gap-4">
                  <div className="w-[50%]">
                    <FloatingInput
                      label={"Type"}
                      name="leave_credit_type"
                      value={toProperCase(
                        initialValues.leave_credit_type || "-",
                      )}
                      disabled
                    />
                  </div>
                  <div className="w-[50%]">
                    <FloatingInput
                      label={"Count"}
                      name="leave_credit_count"
                      value={initialValues.leave_credit_count || 0}
                      disabled
                    />
                  </div>
                </div>
                <div className="w-full relative">
                  {/* Input container */}
                  <div className="relative w-full bg-gray-100 border border-gray-300 rounded-md px-3 pt-4 pb-2">
                    {/* Floating label INSIDE box */}
                    <label className="absolute top-1 left-3 text-xs text-secondary bg-gray-100 px-1">
                      Leave Dates
                    </label>

                    <DatePicker
                      multiple
                      value={leaveDatesForCalendar}
                      currentDate={leaveDatesForCalendar?.[0]}
                      containerClassName="w-full"
                      calendarClassName="w-full"
                      className="w-full"
                      format="DD/MM/YYYY"
                      onChange={(value) => {
                        return;
                      }}
                      inputClass="w-full bg-transparent border-none  p-0 text-sm focus:outline-none focus:ring-0 focus:border-none "
                      mapDays={({ isSelected }) =>
                        isSelected
                          ? {
                              style: {
                                backgroundColor: "#1d4ed8",
                                color: "#fff",
                                borderRadius: "50%",
                                height: "2rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              },
                            }
                          : {}
                      }
                    />
                  </div>
                </div>
              </>
            )}

            {/* Buttons */}
            {!isViewMode && (
              <div className="flex justify-between gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => navigate(fromPage)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg font-semibold text-base"
                  disabled={isSubmitting}
                >
                  {"Cancel"}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary hover:bg-secondary text-white px-3 py-1 rounded-lg"
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default LeaveCreditDebitActions;
